<template>
  <div class="app-version-badge" :title="statusTitle">
    <span class="avb-dot" :class="statusClass"></span>
    <span class="avb-version">v{{ localVersion || '?' }}</span>
    <span v-if="statusLabel" class="avb-status" :class="statusClass">{{ statusLabel }}</span>
  </div>
</template>

<script>
import { firestore } from '@/config/firebase';
import { isUpdateRequired } from '@/util/version';

// Footer badge showing the running web/desktop bundle version and whether it
// is the latest. Reads the same baked VUE_APP_VERSION the force-update overlay
// uses and subscribes to the same app_current_version/web.min_version doc, so
// "Up to date" here means the same thing the overlay enforces. Shows on both
// browser and desktop (Electron) builds so a build can always be confirmed.
export default {
  name: 'AppVersionBadge',
  data() {
    return {
      localVersion: process.env.VUE_APP_VERSION || '',
      minVersion: '',
      loaded: false,
      unsubscribe: null
    };
  },
  computed: {
    behind() {
      return isUpdateRequired(this.localVersion, this.minVersion);
    },
    statusClass() {
      if (!this.loaded || !this.minVersion) return 'is-unknown';
      return this.behind ? 'is-stale' : 'is-latest';
    },
    statusLabel() {
      if (!this.loaded || !this.minVersion) return '';
      return this.behind ? 'Update available' : 'Up to date';
    },
    statusTitle() {
      if (!this.minVersion) return `Version ${this.localVersion || 'unknown'}`;
      return this.behind
        ? `You are on v${this.localVersion}; latest required is v${this.minVersion}`
        : `Running the latest version (v${this.localVersion})`;
    }
  },
  mounted() {
    try {
      this.unsubscribe = firestore
        .collection('app_current_version')
        .doc('web')
        .onSnapshot(snap => {
          this.loaded = true;
          if (!snap.exists) return;
          const d = snap.data() || {};
          this.minVersion = String(d.min_version || '').trim();
        }, () => { this.loaded = true; });
    } catch (e) {
      this.loaded = true;
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
};
</script>

<style scoped>
.app-version-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  font-family: Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
  background: #f1f3f5;
  user-select: none;
}
.avb-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.avb-version {
  color: #061D47;
}
.avb-status {
  font-weight: 500;
}
.avb-dot.is-latest { background: #2e7d32; }
.avb-status.is-latest { color: #2e7d32; }
.avb-dot.is-stale { background: #c62828; }
.avb-status.is-stale { color: #c62828; }
.avb-dot.is-unknown { background: #bbb; }
.avb-status.is-unknown { color: #888; }
</style>
